// Склад сырья (Этап 3, раздел 6.10 ТЗ): остатки по заводам, приходы, движения, инвентаризации, отчёт за период.

import { useState } from "react";
import { addDays, fmtDateTime, fmtNum, localISODate } from "../../shared/format";
import { useLive } from "../../shared/live";
import { TopBar } from "../../shared/shell";
import { Button, Field, Input, Kpi, Loading, ReasonDialog, Select, Tabs, useAsync, useToast } from "../../shared/ui";
import { BalancesTable, can, InventoryDialog, qty, ReceiptDialog, ReceiptsTable, WriteoffDialog, type Balance } from "../components/StockParts";
import { api, live, useCtx } from "../ctx";

type Tab = "balances" | "receipts" | "moves" | "inventories" | "report";

export function StockPage() {
  const { me, refs } = useCtx();
  const toast = useToast();
  const today = localISODate();
  const [tab, setTab] = useState<Tab>("balances");
  const [plant, setPlant] = useState("");
  const [start, setStart] = useState(addDays(today, -7));
  const [end, setEnd] = useState(today);
  const [dialog, setDialog] = useState<"" | "receipt" | "writeoff" | "inventory">("");
  const plants = me.all_plants ? refs.plants : refs.plants.filter((p) => me.plant_ids.includes(p.id));
  const p = plant || undefined;

  const bal = useAsync<{ rows: Balance[]; avg_days: number; warn_days: number }>(() => api.get("/stock/balances", { plant: p }), [plant]);
  const rec = useAsync<any[]>(() => (tab === "receipts" ? api.get("/stock/receipts", { plant: p, start, end }) : Promise.resolve([])), [tab, plant, start, end]);
  const mov = useAsync<any[]>(() => (tab === "moves" ? api.get("/stock/moves", { plant: p, start, end }) : Promise.resolve([])), [tab, plant, start, end]);
  const inv = useAsync<any[]>(() => (tab === "inventories" ? api.get("/stock/inventories", { plant: p }) : Promise.resolve([])), [tab, plant]);
  const rep = useAsync<any>(() => (tab === "report" ? api.get("/stock/report", { plant: p, start, end }) : Promise.resolve(null)), [tab, plant, start, end]);
  const reload = () => { bal.reload(); rec.reload(); mov.reload(); inv.reload(); rep.reload(); };
  useLive(live, ["stock.", "batch."], reload, 800);
  const [cancel, setCancel] = useState<any>(null);

  const rows = bal.data?.rows || [];
  const low = rows.filter((r) => r.level !== "ok");
  const pending = rows.filter((r) => r.pending_lab > 0).length;
  const done = () => { setDialog(""); reload(); };

  return (
    <>
      <TopBar title="Склад сырья">
        {can.receipt(me.role) && <Button variant="primary" onClick={() => setDialog("receipt")}>Приход</Button>}
        {can.receipt(me.role) && <Button onClick={() => setDialog("writeoff")}>Списание</Button>}
        {can.inventory(me.role) && <Button onClick={() => setDialog("inventory")}>Инвентаризация</Button>}
      </TopBar>
      <div className="content">
        <Tabs value={tab} onChange={setTab} items={[
          { value: "balances", label: "Остатки" }, { value: "receipts", label: "Приход" }, { value: "moves", label: "Движения" },
          { value: "inventories", label: "Инвентаризации" }, { value: "report", label: "Отчёт за период" }]} />
        <div className="row" style={{ alignItems: "flex-end" }}>
          {plants.length > 1 && <Field label="Завод"><Select value={plant} onChange={(e) => setPlant(e.target.value)} placeholder="Все заводы" options={plants.map((x) => ({ value: x.id, label: x.name }))} /></Field>}
          {(tab === "receipts" || tab === "moves" || tab === "report") && <>
            <Field label="С"><Input type="date" value={start} onChange={(e) => setStart(e.target.value)} /></Field>
            <Field label="По"><Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} /></Field>
          </>}
          {tab === "report" && <Button onClick={() => api.open("/stock/report/export.xlsx", { plant: p, start, end }, `сырьё_${start}_${end}.xlsx`).catch((e) => toast.error(e))}>Выгрузить в Excel</Button>}
        </div>

        {tab === "balances" && (!bal.data ? <Loading error={bal.error} onRetry={bal.reload} /> : (
          <div className="stack">
            <div className="kpis">
              <Kpi k="Позиций на складе" v={rows.length} />
              <Kpi k="Заканчивается или нет" v={low.length} tone={low.some((r) => r.level === "out") ? "bad" : low.length ? "warn" : undefined}
                s={low.slice(0, 3).map((r) => `${r.material.split(" ")[0]} (${r.plant_name})`).join(", ")} />
              <Kpi k="Ждут входного контроля" v={pending} tone={pending ? "warn" : undefined} />
            </div>
            <BalancesTable rows={rows} showPlant={!plant && plants.length > 1} />
            <p className="small muted">«Расход в сутки» — среднее за {bal.data.avg_days} дн. по рецептурам и списаниям. «Хватит на» — при таком же расходе и без новых приходов; меньше {bal.data.warn_days} дн. — «заканчивается».</p>
          </div>
        ))}

        {tab === "receipts" && (!rec.data ? <Loading error={rec.error} onRetry={rec.reload} /> : (
          <ReceiptsTable rows={rec.data} onCancel={can.cancelReceipt(me.role) ? setCancel : undefined} />
        ))}

        {tab === "moves" && (!mov.data ? <Loading error={mov.error} onRetry={mov.reload} /> : <MovesTable rows={mov.data} />)}

        {tab === "inventories" && (!inv.data ? <Loading error={inv.error} onRetry={inv.reload} /> : (
          inv.data.length === 0 ? <p className="empty">Инвентаризаций ещё не было.</p> : (
            <div className="stack">
              {inv.data.map((i) => (
                <div key={i.id} className="panel">
                  <div className="ph"><strong>{i.plant_name} · {fmtDateTime(i.at)}</strong><span className="small muted">{i.comment}</span></div>
                  <table className="table">
                    <thead><tr><th>Материал</th><th className="num">По учёту</th><th className="num">По факту</th><th className="num">Разница</th></tr></thead>
                    <tbody>{i.lines.map((l: any) => (
                      <tr key={l.material_id}><td>{l.material}</td><td className="num">{qty(l.book, l.unit)}</td><td className="num">{qty(l.fact, l.unit)}</td>
                        <td className={`num${l.diff ? " strong" : ""}`}>{l.diff ? `${l.diff > 0 ? "+" : ""}${fmtNum(l.diff, 3)}` : "—"}{l.diff && l.book ? <span className="small muted"> ({fmtNum((l.diff / l.book) * 100, 1)}%)</span> : null}</td></tr>
                    ))}</tbody>
                  </table>
                </div>
              ))}
            </div>
          )
        ))}

        {tab === "report" && (!rep.data?.rows ? <Loading error={rep.error} onRetry={rep.reload} /> : (
          <div className="stack">
            {rep.data.produced_m3 > 0 && <div className="kpis"><Kpi k="Выпущено бетона за период (по замесам)" v={`${fmtNum(rep.data.produced_m3, 0)} м³`} /></div>}
            <div className="panel table-wrap">
              <table className="table">
                <thead><tr><th>Завод</th><th>Материал</th><th className="num">На начало</th><th className="num">Приход</th><th className="num">Расход по рецептуре</th><th className="num">Прочее</th><th className="num">На конец</th><th className="num">На 1 м³</th></tr></thead>
                <tbody>{rep.data.rows.map((r: any) => (
                  <tr key={r.plant + r.material}>
                    <td>{r.plant}</td><td>{r.material}</td>
                    <td className="num">{qty(r.opening, r.unit)}</td><td className="num">{qty(r.receipts, r.unit)}</td>
                    <td className="num">{qty(r.consumption, r.unit)}</td><td className="num">{r.other ? qty(r.other, r.unit) : "—"}</td>
                    <td className="num strong">{qty(r.closing, r.unit)}</td>
                    <td className="num small">{!plant || !rep.data.produced_m3 ? "" : fmtNum(r.consumption / rep.data.produced_m3, 3)}</td>
                  </tr>
                ))}</tbody>
              </table>
              {rep.data.rows.length === 0 && <p className="empty">За период движений нет.</p>}
            </div>
            <p className="small muted">«Прочее» — инвентаризации, списания вне рецептуры, возвраты поставщику. «На 1 м³» считается, когда выбран один завод.</p>
          </div>
        ))}
      </div>

      {dialog === "receipt" && <ReceiptDialog plants={plants} plantId={plant} onClose={() => setDialog("")} onDone={done} />}
      {dialog === "writeoff" && <WriteoffDialog plants={plants} plantId={plant} onClose={() => setDialog("")} onDone={done} />}
      {dialog === "inventory" && <InventoryDialog plants={plants} plantId={plant} onClose={() => setDialog("")} onDone={done} />}
      {cancel && (
        <ReasonDialog title={`Отменить приход: ${cancel.material}, ${qty(cancel.qty, cancel.unit)}?`} action="Отменить приход" danger
          onCancel={() => setCancel(null)}
          onConfirm={async (reason) => {
            try { await api.post(`/stock/receipts/${cancel.id}/cancel`, { reason }); toast.ok("Приход отменён, остаток уменьшен"); setCancel(null); reload(); } catch (e) { toast.error(e); }
          }} />
      )}
    </>
  );
}

export function MovesTable({ rows }: { rows: any[] }) {
  if (!rows.length) return <p className="empty">Движений за период нет.</p>;
  return (
    <div className="panel table-wrap">
      <table className="table">
        <thead><tr><th>Когда</th><th>Завод</th><th>Материал</th><th>Что</th><th className="num">Количество</th><th>Основание</th><th>Кто</th></tr></thead>
        <tbody>{rows.map((r) => (
          <tr key={r.id}>
            <td className="tabular">{fmtDateTime(r.at)}</td><td>{r.plant_name}</td><td>{r.material}</td><td>{r.kind_ru}</td>
            <td className={`num${r.qty < 0 ? "" : " strong"}`}>{r.qty > 0 ? "+" : ""}{fmtNum(r.qty, 3)} {r.unit}</td>
            <td className="small">{r.what || ""}</td><td className="small muted">{r.user || ""}</td>
          </tr>
        ))}</tbody>
      </table>
      {rows.length >= 2000 && <p className="small muted">Показаны последние 2000 движений — сузьте период или завод.</p>}
    </div>
  );
}
